import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { DozentService } from '../../../services/dozent.service';
import { participantPseudonym } from '../../../helpers/participant-pseudonym';

interface ReviewSubmission {
  id: string;
  learner_user_id: string;
  learner_name?: string;
  learner_email?: string;
  answers: any;
  ai_score?: number | null;
  ai_feedback?: string | null;
  ai_feedback_structured?: StructuredFeedback | null;
  dozent_score?: number | null;
  dozent_feedback?: string | null;
  dozent_feedback_structured?: StructuredFeedback | null;
  status: string;
  submitted_at?: string;
  feedback_released_at?: string | null;
  feedback_release_source?: string | null;
}

interface StructuredFeedback {
  version?: number;
  score?: number;
  max_score?: number;
  questions?: StructuredQuestionFeedback[];
}

interface StructuredQuestionFeedback {
  question_index: number;
  question_id?: string | null;
  question_type?: string | null;
  question_text?: string | null;
  correct?: boolean;
  points?: number;
  max_points?: number;
  feedback?: string | null;
  explanation?: string | null;
  selected?: string[];
  correct_answers?: string[];
  option_results?: StructuredOptionResult[];
  blank_results?: StructuredBlankResult[];
}

interface StructuredOptionResult {
  text: string;
  selected?: boolean;
  correct?: boolean;
  status?: string;
}

interface StructuredBlankResult {
  index?: number;
  submitted?: string;
  expected?: string;
  correct?: boolean;
}

@Component({
  selector: 'app-submission-review-dialog',
  templateUrl: './submission-review-dialog.component.html',
  styleUrl: './submission-review-dialog.component.scss',
})
export class SubmissionReviewDialogComponent implements OnInit {
  @Input() assignmentId!: string;
  /** Optional: when opened from a notification deep-link, the dialog
   *  preselects this submission instead of the first one in the list.
   *  Falls back silently if the id isn't in the loaded set. */
  @Input() preselectSubmissionId?: string;

  loading = true;
  saving = false;

  assignment: any = null;
  exercise: any = null;
  submissions: ReviewSubmission[] = [];
  selected: ReviewSubmission | null = null;

  // Editable fields for the selected submission
  editScore: number | null = null;
  editFeedback = '';

  constructor(
    protected dialogRef: NbDialogRef<SubmissionReviewDialogComponent>,
    private dozentService: DozentService,
    private toastrService: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.loading = true;
    this.dozentService.listAssignmentSubmissions(this.assignmentId).subscribe({
      next: (data: any) => {
        this.assignment = data?.assignment;
        this.exercise = data?.exercise;
        this.submissions = data?.submissions ?? [];
        if (this.submissions.length > 0) {
          const target =
            (this.preselectSubmissionId &&
              this.submissions.find(
                (s) => s.id === this.preselectSubmissionId,
              )) ||
            this.submissions[0];
          this.selectSubmission(target);
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastrService.danger(this.t.instant('toasts.error'), '');
      },
    });
  }

  selectSubmission(s: ReviewSubmission) {
    this.selected = s;
    this.editScore = s.dozent_score ?? s.ai_score ?? null;
    this.editFeedback = s.dozent_feedback ?? s.ai_feedback ?? '';
  }

  releaseStatusKey(submission: ReviewSubmission | null): string {
    if (!submission) {
      return 'pages.dozent.participants.releaseStatusPending';
    }
    if (submission.feedback_release_source === 'dozent') {
      return 'pages.dozent.participants.releaseStatusDozent';
    }
    if (submission.feedback_release_source === 'ai_auto') {
      return 'pages.dozent.participants.releaseStatusAiAuto';
    }
    return 'pages.dozent.participants.releaseStatusPending';
  }

  releaseStatusClass(submission: ReviewSubmission | null): string {
    if (!submission?.feedback_released_at) {
      return 'pending';
    }
    return submission.feedback_release_source === 'ai_auto' ? 'auto' : 'released';
  }

  structuredQuestions(): StructuredQuestionFeedback[] {
    const structured =
      this.selected?.dozent_feedback_structured ??
      this.selected?.ai_feedback_structured ??
      null;
    return Array.isArray(structured?.questions) ? structured.questions : [];
  }

  structuredFeedback(): StructuredFeedback | null {
    return (
      this.selected?.dozent_feedback_structured ??
      this.selected?.ai_feedback_structured ??
      null
    );
  }

  hasStructuredReview(): boolean {
    return this.structuredQuestions().length > 0;
  }

  resultScore(): number | null {
    const structuredScore = this.structuredFeedback()?.score;
    return this.selected?.dozent_score ?? this.selected?.ai_score ?? structuredScore ?? null;
  }

  resultMaxScore(): number | null {
    const structuredMax = this.structuredFeedback()?.max_score;
    return structuredMax ?? this.exercise?.max_score ?? null;
  }

  resultFeedback(): string {
    return this.selected?.dozent_feedback ?? this.selected?.ai_feedback ?? '';
  }

  resultSourceLabelKey(): string {
    return this.resultSource() === 'dozent'
      ? 'pages.dozent.participants.resultBadgeDozent'
      : 'pages.dozent.participants.resultBadgeAi';
  }

  resultSourceClass(): string {
    return this.resultSource() === 'dozent' ? 'source-dozent' : 'source-ai';
  }

  private resultSource(): 'ai' | 'dozent' {
    if (
      this.selected?.feedback_release_source === 'dozent' ||
      this.selected?.dozent_score != null ||
      !!this.selected?.dozent_feedback ||
      !!this.selected?.dozent_feedback_structured
    ) {
      return 'dozent';
    }
    return 'ai';
  }

  questionStatusClass(result: StructuredQuestionFeedback): string {
    return result?.correct ? 'is-correct' : 'is-incorrect';
  }

  hasOptionResults(result: StructuredQuestionFeedback): boolean {
    return Array.isArray(result?.option_results) && result.option_results.length > 0;
  }

  hasBlankResults(result: StructuredQuestionFeedback): boolean {
    return Array.isArray(result?.blank_results) && result.blank_results.length > 0;
  }

  optionStatusClass(option: StructuredOptionResult): string {
    switch (option?.status) {
      case 'correct_selected':
        return 'correct-selected';
      case 'incorrect_selected':
        return 'incorrect-selected';
      case 'correct_missing':
        return 'correct-missing';
      default:
        return 'neutral';
    }
  }

  optionStatusLabelKey(option: StructuredOptionResult): string {
    switch (option?.status) {
      case 'correct_selected':
        return 'pages.dozent.participants.answerCorrectSelected';
      case 'incorrect_selected':
        return 'pages.dozent.participants.answerWrongSelected';
      case 'correct_missing':
        return 'pages.dozent.participants.answerCorrectMissing';
      default:
        return 'pages.dozent.participants.answerNeutral';
    }
  }

  optionStatusSymbol(option: StructuredOptionResult): string {
    switch (option?.status) {
      case 'correct_selected':
      case 'correct_missing':
        return '✓';
      case 'incorrect_selected':
        return '✕';
      default:
        return '';
    }
  }

  formatValues(values?: string[] | null): string {
    if (!Array.isArray(values)) return '';
    return values.map((value) => String(value ?? '')).filter(Boolean).join(', ');
  }

  submissionLearnerLabel(submission: ReviewSubmission): string {
    return submission.learner_name || participantPseudonym({
      id: submission.id,
      learner_user_id: submission.learner_user_id,
      email: submission.learner_email,
    });
  }

  questionList(): any[] {
    const c = this.exercise?.content;
    if (!c) return [];
    if (Array.isArray(c?.questions)) return c.questions;
    if (Array.isArray(c)) return c;
    return [];
  }

  /**
   * Resolve the user-visible question text. Editor saves
   * `problemStatement` (camelCase); legacy / external formats might
   * use `problem_statement`, `question`, `text`, or `title`. Fall
   * through them all so the dozent always sees the prompt instead
   * of a generic "Question N".
   */
  getQuestionText(q: any, index: number): string {
    return (
      q?.problemStatement ??
      q?.problem_statement ??
      q?.question ??
      q?.text ??
      q?.title ??
      `Question ${index + 1}`
    );
  }

  /**
   * Resolve the correct-answer hint to display under the learner's
   * answer. For multiple-choice/true-false the editor stores
   * `answer: [{text, is_correct}]` — pull only the correct entries.
   * For fill-in-blank it's a string-array, joined visibly. For
   * open-ended it's a plain string.
   */
  getCorrectAnswer(q: any): string {
    const ans = q?.answer ?? q?.correct_answer;
    if (ans == null || ans === '') return '';
    if (typeof ans === 'string') return ans;
    if (ans && typeof ans === 'object' && Array.isArray(ans.correct_answers)) {
      return ans.correct_answers
        .map((v: any) => String(v ?? ''))
        .filter(Boolean)
        .join(', ');
    }
    if (Array.isArray(ans)) {
      // [{text, is_correct}] → only correct ones, comma-joined.
      if (ans.length && typeof ans[0] === 'object' && 'is_correct' in ans[0]) {
        return ans
          .filter((o: any) => o?.is_correct)
          .map((o: any) => o?.text ?? '')
          .filter(Boolean)
          .join(', ');
      }
      // Plain string array (fill-in-blank).
      return ans.map((v: any) => String(v ?? '')).filter(Boolean).join(', ');
    }
    return String(ans);
  }

  answerFor(q: any, index: number): string {
    if (!this.selected?.answers) return '';
    const a = this.selected.answers;
    const raw =
      a[q?.id] ?? a[index] ?? a[`q${index}`] ?? a[String(index)] ?? '';
    return this.formatAnswer(raw);
  }

  /**
   * Render the learner's submitted answer as plain user-friendly
   * text instead of raw JSON. Handles:
   *   • string                        → as-is
   *   • string[] (fill-in-blank)      → comma-joined
   *   • {text, is_correct} (MC pick)  → just .text
   *   • object map (legacy)           → values joined
   *   • anything else                 → JSON-stringify with no fences
   */
  private formatAnswer(raw: any): string {
    if (raw == null || raw === '') return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
    if (Array.isArray(raw)) {
      return raw
        .map((v) => (typeof v === 'string' ? v : v?.text ?? JSON.stringify(v)))
        .filter((v) => v !== '')
        .join(', ');
    }
    if (typeof raw === 'object') {
      if ('text' in raw) return String(raw.text ?? '');
      // Plain {key: value} maps — join values, skip keys.
      const vals = Object.values(raw).map((v) => (typeof v === 'string' ? v : JSON.stringify(v)));
      return vals.filter((v) => v !== '').join(', ');
    }
    return String(raw);
  }

  saveFeedback() {
    if (!this.selected) return;
    this.saving = true;
    this.dozentService
      .saveSubmissionFeedback(this.selected.id, {
        dozent_score: this.editScore,
        dozent_feedback: this.editFeedback,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.toastrService.success(
            this.t.instant('pages.dozent.participants.feedbackSaved'),
            ''
          );
          // Update local state
          if (this.selected) {
            this.selected.dozent_score = this.editScore;
            this.selected.dozent_feedback = this.editFeedback;
            this.selected.status = 'dozent_reviewed';
            this.selected.feedback_released_at = new Date().toISOString();
            this.selected.feedback_release_source = 'dozent';
          }
        },
        error: () => {
          this.saving = false;
          this.toastrService.danger(this.t.instant('toasts.error'), '');
        },
      });
  }

  close() {
    this.dialogRef.close();
  }
}
